import React, { useEffect, useState } from 'react'
import {
  fetchBackgrounds,
  deleteBackground,
  updateBackground,
  createBackground,
  fetchPhotoBooths
} from 'helpers/backend_helper'
import { APIClient } from 'helpers/api_helper'
import { Container } from 'reactstrap'
import BreadCrumb from 'Components/Common/BreadCrumb'
import { toast } from 'react-toastify'
import BackgroundsTable from './BackgroundsTable'
import BackgroundModal from './BackgroundModal'

export interface IBackground {
  _id?: string
  name: string
  imageLink: string
  category: string
  translations: Record<string, string>
  parts: any[]
  isActive: boolean
  isActiveFor: string[]
}

export interface ICategory {
  _id: string
  name: string
}

export interface IPhotoBooth {
  _id: string
  name: string
}

const Backgrounds = () => {
  document.title = 'Backgrounds | SocialBooth - Admin Dashboard'

  const [fetching, setFetching] = useState<boolean>(false)
  const [backgroundRecords, setBackgroundRecords] = useState<IBackground[]>([])
  const [categories, setCategories] = useState<ICategory[]>([])
  const [photoBooths, setPhotoBooths] = useState<IPhotoBooth[]>([])
  const [openBackgroundPopup, setOpenBackgroundPopup] = useState<boolean>(false)
  const [currentBackground, setCurrentBackground] = useState<IBackground>()
  const [editMode, setEditMode] = useState<boolean>(false)

  const fetchBackgroundRecords = async () => {
    try {
      setFetching(true)
      const response = await fetchBackgrounds()
      if (Array.isArray(response.data)) {
        setBackgroundRecords(response.data)
      } else if (response.data.success) {
        setBackgroundRecords(response.data.data)
      }
      setFetching(false)
    } catch (e: any) {
      toast.error(typeof e === 'string' ? e : (e.message || 'Arka planlar yüklenirken hata oluştu'))
      setFetching(false)
    }
  }

  const fetchCategoryList = async () => {
    try {
      const api = new APIClient()
      const res: any = await api.get('/api/v1/backgrounds/categories', null)
      let catNames: string[] = []
      if (res?.success && Array.isArray(res.data)) {
        catNames = res.data
      } else if (Array.isArray(res?.data?.data)) {
        catNames = res.data.data
      } else if (Array.isArray(res?.data)) {
        catNames = res.data
      }
      setCategories(
        catNames.filter(Boolean).map((name: string) => ({ _id: name, name }))
      )
    } catch (e: any) {
      console.error('Kategoriler yüklenemedi:', e)
    }
  }

  const fetchBoothList = async () => {
    try {
      const response = await fetchPhotoBooths()
      if (Array.isArray(response.data)) {
        setPhotoBooths(response.data)
      } else if (response.data.success) {
        setPhotoBooths(response.data.data)
      }
    } catch (e: any) {
      console.error('Booth listesi yüklenemedi:', e)
    }
  }

  useEffect(() => {
    fetchBackgroundRecords()
    fetchCategoryList()
    fetchBoothList()
  }, [])

  const handleOpenDetails = (id: string) => {
    const selectedBackground = backgroundRecords.find((bg) => bg._id === id)
    if (selectedBackground) {
      setCurrentBackground(selectedBackground)
      setOpenBackgroundPopup(true)
    }
  }

  const handleCreateOrUpdateBackground = async (params: any) => {
    try {
      const id = params instanceof FormData ? params.get('_id') as string : params._id
      const response: any = editMode
        ? await updateBackground(params, id)
        : await createBackground(params)

      if (response.success) {
        if (editMode) {
          const updatedRecords = backgroundRecords.map((bg: IBackground) => {
            if (bg._id === response.data._id) {
              return response.data
            }
            return bg
          })
          setBackgroundRecords(updatedRecords)
        } else {
          setBackgroundRecords([response.data, ...backgroundRecords])
        }
        fetchCategoryList()
        return 'SUCCESSFULL'
      }
      return response.msg || 'Hata oluştu'
    } catch (e: any) {
      return typeof e === 'string' ? e : (e.message || 'Arka plan kaydedilirken hata oluştu')
    }
  }

  const handleRenameCategory = async (oldName: string, newName: string) => {
    try {
      const api = new APIClient()
      const res: any = await api.put('/api/v1/backgrounds/renameCategory', { oldName, newName })
      if (res?.success) {
        toast.success(`Kategori "${oldName}" → "${newName}" olarak güncellendi (${res.modifiedCount} kayıt)`)
        fetchBackgroundRecords()
        fetchCategoryList()
      } else {
        toast.error(res?.msg || 'Kategori güncellenemedi')
      }
    } catch (e: any) {
      toast.error(typeof e === 'string' ? e : 'Kategori güncellenirken hata oluştu')
    }
  }

  const handleDeleteBackground = async (id: string) => {
    try {
      const response: any = await deleteBackground(id)
      if (response.success) {
        setBackgroundRecords(backgroundRecords.filter((bg) => bg._id !== id))
        fetchCategoryList()
        return 'SUCCESSFULL'
      }
      return response.msg || 'Hata oluştu'
    } catch (e: any) {
      toast.error(typeof e === 'string' ? e : (e.message || 'Arka plan silinirken hata oluştu'))
      return 'FALSE'
    }
  }

  return (
    <React.Fragment>
      <div className='page-content'>
        <Container fluid>
          <BreadCrumb title='Backgrounds' pageTitle='Asset Management' />
          <BackgroundsTable
            backgroundRecords={backgroundRecords}
            setOpenBackgroundPopup={setOpenBackgroundPopup}
            handleOpenDetails={handleOpenDetails}
            setEditMode={setEditMode}
            handleDeleteBackground={handleDeleteBackground}
            handleRenameCategory={handleRenameCategory}
            categories={categories}
            fetching={fetching}
          />
          <BackgroundModal
            isOpen={openBackgroundPopup}
            setOpenPopup={setOpenBackgroundPopup}
            handleCreateOrUpdateBackground={handleCreateOrUpdateBackground}
            backgroundRecord={currentBackground}
            editMode={editMode}
            categories={categories}
            photoBooths={photoBooths}
          />
        </Container>
      </div>
    </React.Fragment>
  )
}

export default Backgrounds
